import React, { useState, useRef, useEffect } from 'react';
import { Send, Bot, User, Loader2, Settings, Activity, ChefHat, Calendar, X, KeyRound, TrendingUp, Dumbbell, Flame, Sparkles, CheckCircle2, Circle, ChevronDown, ChevronUp, ArrowRight, Baby, Ruler, Weight, BookOpen, Search, ShoppingBag, MapPin, Stethoscope, AlertTriangle, UserCircle, MessageSquare, ClipboardList, Edit3, HeartHandshake } from 'lucide-react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import ReactMarkdown from 'react-markdown';

// --- 0. 配置常量 ---
const DEFAULT_API_KEY = "4ea26bb5-c773-4e31-ad64-1ecd73ae4057";
const API_URL = "https://ark.cn-beijing.volces.com/api/v3/chat/completions";
const DEFAULT_MODEL = "deepseek-v3-2-251201";

// --- 1. 数据结构 ---

interface UserProfile {
  last_menstrual_date: string | null;
  height_cm: number;
  weight_kg: number;
  allergies: string[];
  location?: string;
}

interface BloodSugarRecord {
  timestamp: string;
  value: number;
  measure_type: 'fasting' | 'postprandial_1h' | 'postprandial_2h' | 'random';
  note: string;
}

interface MealItem { name: string; portion: string; calories: number; }
interface DailyMeals { [key: string]: MealItem[] } // key: "早餐", "午餐", "晚餐"
interface WeeklyMeals { [key: string]: DailyMeals } // key: "周一" ...

interface ExerciseItem { activity: string; duration: string; intensity: string; }
interface DailyExercise { items: ExerciseItem[]; notes: string; }
interface WeeklyExercises { [key: string]: DailyExercise }

interface UserContext {
  profile: UserProfile;
  blood_sugar_history: BloodSugarRecord[];
  meal_plan: WeeklyMeals;
  exercise_plan: WeeklyExercises;
}

interface TimelineStep {
  label: string;
  status: 'pending' | 'running' | 'done';
}

// 辅助函数：键值归一化 (解决 AI 输出 "星期一" 系统不认的问题)
const normalizeDay = (key: string): string => {
  const map: Record<string, string> = {
    "monday": "周一", "mon": "周一", "星期一": "周一", "周1": "周一",
    "tuesday": "周二", "tue": "周二", "星期二": "周二", "周2": "周二",
    "wednesday": "周三", "wed": "周三", "星期三": "周三", "周3": "周三",
    "thursday": "周四", "thu": "周四", "星期四": "周四", "周4": "周四",
    "friday": "周五", "fri": "周五", "星期五": "周五", "周5": "周五",
    "saturday": "周六", "sat": "周六", "星期六": "周六", "周6": "周六",
    "sunday": "周日", "sun": "周日", "星期日": "周日", "周7": "周日", "周天": "周日"
  };
  return map[key.toLowerCase().trim()] || key.trim();
};

const normalizeMeal = (key: string): string => {
  const map: Record<string, string> = {
    "breakfast": "早餐", "早饭": "早餐",
    "lunch": "午餐", "午饭": "午餐", "中饭": "午餐",
    "dinner": "晚餐", "晚饭": "晚餐",
    "snack": "加餐"
  };
  return map[key.toLowerCase().trim()] || key.trim();
};

// 工具名称映射
const TOOL_NAMES: Record<string, string> = {
  "update_user_profile": "更新档案",
  "record_blood_sugar": "记录血糖",
  "manage_meal_plan": "调整食谱",
  "manage_exercise_plan": "调整运动",
  "search_knowledge_base": "查阅知识库",
  "recommend_products": "严选商品推荐",
  "find_nearby_services": "筛选周边服务",
  "get_checkup_schedule": "计算产检计划"
};

// 默认数据...
const DEFAULT_MEAL_PLAN: WeeklyMeals = {
  "周一": {
    "早餐": [{ name: "全麦面包", portion: "2片", calories: 150 }, { name: "煮鸡蛋", portion: "1个", calories: 70 }, { name: "低脂牛奶", portion: "250ml", calories: 110 }],
    "午餐": [{ name: "杂粮饭", portion: "150g", calories: 174 }, { name: "清蒸鲈鱼", portion: "100g", calories: 120 }, { name: "蒜蓉西兰花", portion: "200g", calories: 60 }],
    "晚餐": [{ name: "小米粥", portion: "1碗", calories: 100 }, { name: "凉拌鸡丝", portion: "80g", calories: 130 }]
  },
  "周二": { "早餐": [{ name: "燕麦粥", portion: "1碗", calories: 150 }, { name: "坚果", portion: "15g", calories: 90 }], "午餐": [{ name: "荞麦面", portion: "1碗", calories: 200 }, { name: "白灼虾", portion: "8只", calories: 100 }, { name: "清炒时蔬", portion: "200g", calories: 50 }], "晚餐": [{ name: "蒸红薯", portion: "1个", calories: 130 }, { name: "番茄蛋汤", portion: "1碗", calories: 70 }] },
  "周三": { "早餐": [{ name: "全麦馒头", portion: "1个", calories: 180 }, { name: "无糖豆浆", portion: "1杯", calories: 60 }], "午餐": [{ name: "糙米饭", portion: "150g", calories: 170 }, { name: "炖牛肉", portion: "80g", calories: 150 }, { name: "凉拌黄瓜", portion: "150g", calories: 30 }], "晚餐": [{ name: "玉米", portion: "1根", calories: 110 }, { name: "豆腐汤", portion: "1碗", calories: 60 }] },
  "周四": { "早餐": [{ name: "煮玉米", portion: "1根", calories: 110 }, { name: "酸奶", portion: "100g", calories: 70 }], "午餐": [{ name: "黑米饭", portion: "150g", calories: 170 }, { name: "炒鸡胸肉", portion: "100g", calories: 130 }, { name: "炒菠菜", portion: "200g", calories: 50 }], "晚餐": [{ name: "南瓜粥", portion: "1碗", calories: 90 }, { name: "蒸蛋羹", portion: "1碗", calories: 80 }] },
  "周五": { "早餐": [{ name: "全麦面包", portion: "2片", calories: 150 }, { name: "煎蛋", portion: "1个", calories: 90 }], "午餐": [{ name: "二米饭", portion: "150g", calories: 175 }, { name: "红烧鱼块", portion: "100g", calories: 140 }, { name: "白灼菜心", portion: "200g", calories: 40 }], "晚餐": [{ name: "蔬菜沙拉", portion: "1份", calories: 120 }, { name: "全麦饼干", portion: "2片", calories: 70 }] },
  "周六": { "早餐": [{ name: "牛奶", portion: "250ml", calories: 110 }, { name: "全麦三明治", portion: "1个", calories: 220 }], "午餐": [{ name: "意面", portion: "150g", calories: 200 }, { name: "煎牛排", portion: "100g", calories: 200 }, { name: "蔬菜汤", portion: "1碗", calories: 50 }], "晚餐": [{ name: "杂粮粥", portion: "1碗", calories: 100 }, { name: "凉拌木耳", portion: "100g", calories: 40 }] },
  "周日": { "早餐": [{ name: "豆浆", portion: "1杯", calories: 60 }, { name: "菜包", portion: "1个", calories: 150 }], "午餐": [{ name: "蒸饺", portion: "8个", calories: 320 }, { name: "紫菜汤", portion: "1碗", calories: 30 }], "晚餐": [{ name: "山药", portion: "100g", calories: 60 }, { name: "清蒸排骨", portion: "3块", calories: 150 }] }
};

const DEFAULT_EXERCISE_PLAN: WeeklyExercises = {
  "周一": { items: [{ activity: "餐后散步", duration: "20分钟", intensity: "低" }, { activity: "孕妇瑜伽", duration: "15分钟", intensity: "低" }], notes: "注意心率不要过快" },
  "周二": { items: [{ activity: "快走", duration: "30分钟", intensity: "中" }], notes: "选择空气清新的公园" },
  "周三": { items: [{ activity: "上肢力量训练(哑铃)", duration: "15分钟", intensity: "低" }, { activity: "骨盆底肌训练", duration: "10分钟", intensity: "低" }], notes: "使用1-2kg小哑铃" },
  "周四": { items: [{ activity: "游泳或水中漫步", duration: "30分钟", intensity: "中" }], notes: "注意水温适宜" },
  "周五": { items: [{ activity: "孕妇操", duration: "20分钟", intensity: "中" }], notes: "跟着视频做，动作幅度不要太大" },
  "周六": { items: [{ activity: "户外散步", duration: "40分钟", intensity: "低" }], notes: "家人陪伴，心情愉快" },
  "周日": { items: [{ activity: "冥想放松", duration: "20分钟", intensity: "极低" }], notes: "舒缓身心，准备新的一周" }
};

const INITIAL_CONTEXT: UserContext = {
  profile: { last_menstrual_date: null, height_cm: 160, weight_kg: 55, allergies: [], location: "北京" },
  blood_sugar_history: [],
  meal_plan: DEFAULT_MEAL_PLAN,
  exercise_plan: DEFAULT_EXERCISE_PLAN
};

// --- 2. 核心业务逻辑 ---

function calculatePregnancyWeek(lmp: string | null) {
  if (!lmp) return null;
  let start: Date | null = null;
  
  const d = new Date(lmp);
  if (!isNaN(d.getTime())) {
    start = d;
  } else {
    const yyyymmdd = /^(\d{4})(\d{2})(\d{2})$/;
    const match = lmp.match(yyyymmdd);
    if (match) {
      start = new Date(`${match[1]}-${match[2]}-${match[3]}`);
    } else {
      const parts = lmp.match(/\d+/g);
      if (parts && parts.length >= 3 && parts[0].length === 4) {
        start = new Date(`${parts[0]}-${parts[1]}-${parts[2]}`);
      } else {
        return null;
      }
    }
  }

  if (isNaN(start.getTime())) return null;

  const now = new Date();
  const diffTime = now.getTime() - start.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return { weeks: 0, days: 0, dueDate: "日期在未来?", error: true, message: "这是未来的日期哦，请检查年份~" };
  }

  const weeks = Math.floor(diffDays / 7);
  const days = diffDays % 7;
  const dueDate = new Date(start);
  dueDate.setDate(dueDate.getDate() + 280);
  
  return { 
    weeks, 
    days, 
    dueDate: dueDate.toLocaleDateString(),
    formattedLMP: start.toLocaleDateString()
  };
}

function checkBloodSugarStatus(value: number, type: string) {
  // 1. 危急低血糖 (Priority 1) - 低于 3.9 mmol/L
  if (value < 3.9) {
    return { status: 'critical_low', label: '危急低血糖', limit: 3.9 };
  }

  // 2. 危急高血糖 (Priority 1) - 高于 13.9 mmol/L
  if (value > 13.9) {
    return { status: 'critical_high', label: '危急高血糖', limit: 13.9 };
  }

  // 3. 常规红绿灯判断 (Priority 2)
  const standards: Record<string, number> = {
    "fasting": 5.1,
    "postprandial_1h": 10.0,
    "postprandial_2h": 8.5,
    "random": 7.8
  };

  const limit = standards[type] || 7.8;

  if (value > limit) {
    return { status: 'warning', label: '偏高', limit };
  }

  return { status: 'normal', label: '正常', limit };
}

function cn(...inputs: (string | undefined | null | false)[]) { return twMerge(clsx(inputs)); }

// --- 3. Agent 工具定义 ---

const DOUBAO_TOOLS = [
  {
    type: "function",
    function: {
      name: "update_user_profile",
      description: "更新用户档案。必须将日期转换为 'YYYY-MM-DD' 格式。",
      parameters: {
        type: "object",
        properties: {
          last_menstrual_date: { type: "string" },
          height_cm: { type: "number" },
          weight_kg: { type: "number" },
          allergies: { type: "string" },
          location: { type: "string" }
        }
      }
    }
  },
  {
    type: "function",
    function: {
      name: "record_blood_sugar",
      description: "记录血糖值。",
      parameters: {
        type: "object",
        properties: {
          value: { type: "number" },
          type: { type: "string", enum: ["fasting", "postprandial_1h", "postprandial_2h", "random"] }
        },
        required: ["value", "type"]
      }
    }
  },
  {
    type: "function",
    function: {
      name: "manage_meal_plan",
      // 优化工具描述：强调如何构造 updates
      description: "管理食谱。Action: 'get'为查询; 'create'为重置/生成标准食谱; 'update'为修改。Update时，updates 参数必须是对象，key为'周一'等，value为该日要修改的餐次对象。例如：{'周一': {'早餐': [{'name':'燕麦',...}]}}",
      parameters: {
        type: "object",
        properties: {
          action: { type: "string", enum: ["get", "create", "update"], description: "操作类型" },
          updates: { 
            type: "object", 
            description: "仅在 action='update' 时使用。结构：{'周几': {'餐次': [食物列表]}}",
            nullable: true
          }
        },
        required: ["action"]
      }
    }
  },
  {
    type: "function",
    function: {
      name: "manage_exercise_plan",
      description: "管理运动计划。Action: 'get'为查询; 'create'为重置; 'update'为修改。",
      parameters: {
        type: "object",
        properties: {
          action: { type: "string", enum: ["get", "create", "update"] },
          updates: { type: "object", nullable: true }
        },
        required: ["action"]
      }
    }
  },
  {
    type: "function",
    function: {
      name: "search_knowledge_base",
      description: "搜索孕期知识库。**仅用于**回答复杂的医学问题、饮食禁忌（能不能吃...）、症状咨询。简单闲聊不要调用。",
      parameters: {
        type: "object",
        properties: {
          query: { type: "string" }
        },
        required: ["query"]
      }
    }
  },
  {
    type: "function",
    function: {
      name: "recommend_products",
      description: "推荐控糖商品。",
      parameters: {
        type: "object",
        properties: {
          category: { type: "string" },
          needs: { type: "string" }
        },
        required: ["category"]
      }
    }
  },
  {
    type: "function",
    function: {
      name: "find_nearby_services",
      description: "查找周边服务。",
      parameters: {
        type: "object",
        properties: {
          service_type: { type: "string", enum: ["care_center", "nanny", "hospital"] },
          location: { type: "string" }
        },
        required: ["service_type"]
      }
    }
  },
  {
    type: "function",
    function: {
      name: "get_checkup_schedule",
      description: "生成产检计划。仅当用户明确询问产检计划相关问题时使用。",
      parameters: {
        type: "object",
        properties: {
          current_week: { type: "number" },
          current_day: { type: "number" }
        },
        required: ["current_week"]
      }
    }
  }
];

// --- 4. 主组件 ---

export default function App() {
  const [messages, setMessages] = useState<any[]>([
    { id: 'init', role: 'assistant', content: '' } // 初始消息置空，使用 Card 渲染
  ]);
  const [input, setInput] = useState("");
  const [steps, setSteps] = useState<TimelineStep[]>([]);
  const [apiKey, setApiKey] = useState(DEFAULT_API_KEY);
  const [showSettings, setShowSettings] = useState(false);
  const [showProfile, setShowProfile] = useState(false); 
  
  const [context, setContext] = useState<UserContext>(INITIAL_CONTEXT);
  const contextRef = useRef<UserContext>(INITIAL_CONTEXT);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lastUserIndex = messages.map(m => m.role).lastIndexOf('user');

  // 初始化加载
  useEffect(() => {
    const savedKey = localStorage.getItem("doubao_api_key");
    if (savedKey) setApiKey(savedKey);
    else if (!DEFAULT_API_KEY) setShowSettings(true);

    const savedContext = localStorage.getItem("pregnancy_context");
    if (savedContext) {
      try {
        const parsed = JSON.parse(savedContext);
        const merged = { ...INITIAL_CONTEXT, ...parsed };
        setContext(merged);
        contextRef.current = merged; 
      } catch (e) { console.error(e); }
    }
  }, []);

  // 监听 context 变化
  useEffect(() => {
    contextRef.current = context;
    localStorage.setItem("pregnancy_context", JSON.stringify(context));
  }, [context]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, steps]);

  // 工具实现
  const toolsImpl = {
    async update_user_profile(args: any) {
      const pg = calculatePregnancyWeek(args.last_menstrual_date);
      if (!pg || pg.error) {
        return JSON.stringify({ success: false, message: pg?.message || "日期格式识别失败，请尝试标准格式如 2025-05-20。" });
      }

      const newProfile = { 
        ...contextRef.current.profile, 
        ...args, 
        last_menstrual_date: pg.formattedLMP 
      };
      
      if (args.allergies && typeof args.allergies === 'string') newProfile.allergies = args.allergies.split(/[,，]/);
      
      setContext(prev => ({ ...prev, profile: newProfile }));
      contextRef.current = { ...contextRef.current, profile: newProfile };

      return JSON.stringify({
        success: true,
        type: "user_profile",
        data: { ...newProfile, current_week: pg?.weeks, current_day: pg?.days, due_date: pg?.dueDate },
        message: `档案更新成功。当前孕 ${pg.weeks} 周 ${pg.days} 天。`
      });
    },

    async record_blood_sugar(args: any) {
      const check = checkBloodSugarStatus(args.value, args.type);

      let aiAnalysis = "数值正常，继续保持！";
      let actionItems: string[] = [];

      // AI 智能归因逻辑
      if (check.status === 'critical_low') {
        aiAnalysis = "⚠️ 检测到危急低血糖！请立即停止活动，执行【15-15】原则：立即补充 15g 糖分（如葡萄糖粉、果汁），15 分钟后复测血糖。";
        actionItems = ["立即进食15g糖分", "15分钟后复测"];
      } else if (check.status === 'critical_high') {
        const todayKey = ["周日","周一","周二","周三","周四","周五","周六"][new Date().getDay()];
        const todayMeals = contextRef.current.meal_plan[todayKey];
        const carbs = todayMeals ? Object.values(todayMeals).flat().map((m: any) => m.name).join('、') : "常规饮食";
        aiAnalysis = `检测到危急高血糖（${args.value} mmol/L），需警惕酮症酸中毒风险。建议：① 立即就医；② 暂停碳水摄入；③ 多饮水并适度活动。您的今日饮食（${todayKey}）中碳水摄入可能偏高，建议减少精制主食，增加蔬菜和优质蛋白。`;
        actionItems = ["立即就医", "暂停碳水", "多饮水"];
      } else if (check.status === 'warning') {
        const todayKey = ["周日","周一","周二","周三","周四","周五","周六"][new Date().getDay()];
        const todayMeals = contextRef.current.meal_plan[todayKey];
        const carbs = todayMeals ? Object.values(todayMeals).flat().map((m: any) => m.name).join('、') : "常规饮食";
        aiAnalysis = `检测到血糖${check.label}（${args.value} mmol/L）。根据您的今日食谱分析，可能是碳水摄入略多或缺乏膳食纤维包裹。建议下一餐增加绿叶蔬菜比例（如菠菜、西兰花），减少精制主食，并饭后散步 20 分钟。`;
        actionItems = ["增加蔬菜", "减少主食", "饭后散步"];
      }

      // 构建记录对象
      const newRecord = {
        id: Date.now().toString(),
        timestamp: new Date().toISOString(),
        value: args.value,
        measure_type: args.type,
        status: check.status,
        note: check.label,
        ai_analysis: aiAnalysis,
        action_items: actionItems
      };

      setContext(prev => ({ ...prev, blood_sugar_history: [...prev.blood_sugar_history, newRecord] }));
      contextRef.current = { ...contextRef.current, blood_sugar_history: [...contextRef.current.blood_sugar_history, newRecord] };

      return JSON.stringify({
        success: true,
        type: "blood_sugar_card",
        data: newRecord,
        message: `记录成功: ${check.label}`
      });
    },

    async manage_meal_plan(args: any) {
      // 1. Create: 生成新食谱 (直接加载默认模板)
      if (args.action === 'create') {
         const newPlan = DEFAULT_MEAL_PLAN;
         setContext(prev => ({ ...prev, meal_plan: newPlan }));
         contextRef.current = { ...contextRef.current, meal_plan: newPlan };
         return JSON.stringify({ success: true, type: "meal_plan", data: newPlan, message: "已为您生成全新的科学控糖食谱。" });
      }

      // 2. Update: 增量修改
      if (args.action === 'update' && args.updates) {
        const currentPlan = JSON.parse(JSON.stringify(contextRef.current.meal_plan)); // Deep copy to avoid mutation issues
        let modificationSummary = "";

        // 遍历 updates 对象
        for (const dayRaw in args.updates) {
          const day = normalizeDay(dayRaw); // 归一化 "星期一" -> "周一"
          
          if (currentPlan[day]) {
            const dayUpdates = args.updates[dayRaw];
            for (const mealRaw in dayUpdates) {
              const meal = normalizeMeal(mealRaw); // 归一化 "breakfast" -> "早餐"
              
              // 直接覆盖该餐次的食物列表
              if (currentPlan[day][meal] !== undefined) { // 确保该餐次存在才修改
                 currentPlan[day][meal] = dayUpdates[mealRaw];
                 modificationSummary += `${day}${meal} `;
              }
            }
          }
        }

        if (modificationSummary === "") {
             return JSON.stringify({ success: false, message: "未找到对应的日期或餐次，请确认输入（如：周一早餐）。" });
        }

        setContext(prev => ({ ...prev, meal_plan: currentPlan }));
        contextRef.current = { ...contextRef.current, meal_plan: currentPlan };

        return JSON.stringify({ success: true, type: "meal_plan", data: args.updates, message: `已成功修改：${modificationSummary}` });
      }
      
      // 3. Get: 获取当前
      return JSON.stringify({ success: true, type: "meal_plan", data: contextRef.current.meal_plan, message: "这是您的食谱计划。" });
    },

    async manage_exercise_plan(args: any) {
      if (args.action === 'create') {
         const newPlan = DEFAULT_EXERCISE_PLAN;
         setContext(prev => ({ ...prev, exercise_plan: newPlan }));
         contextRef.current = { ...contextRef.current, exercise_plan: newPlan };
         return JSON.stringify({ success: true, type: "exercise_plan", data: newPlan, message: "已为您生成全新的运动计划。" });
      }

      if (args.action === 'update' && args.updates) {
        const currentPlan = JSON.parse(JSON.stringify(contextRef.current.exercise_plan));
        let modificationSummary = "";

        for (const dayRaw in args.updates) {
          const day = normalizeDay(dayRaw);
          if (currentPlan[day]) {
            // 覆盖当天的运动项目
            currentPlan[day] = { ...currentPlan[day], ...args.updates[dayRaw] };
            modificationSummary += `${day} `;
          }
        }
        setContext(prev => ({ ...prev, exercise_plan: currentPlan }));
        contextRef.current = { ...contextRef.current, exercise_plan: currentPlan };
        return JSON.stringify({ success: true, type: "exercise_plan", data: args.updates, message: `运动计划已更新：${modificationSummary}` });
      }
      return JSON.stringify({ success: true, type: "exercise_plan", data: contextRef.current.exercise_plan, message: "这是您的运动计划。" });
    },

    async search_knowledge_base(args: any) {
      await new Promise(r => setTimeout(r, 1000));
      
      try {
        const res = await fetch(API_URL, {
          method: "POST", 
          headers: { 
            "Content-Type": "application/json",
            "Authorization": `Bearer ${apiKey}`
          },
          body: JSON.stringify({ 
            model: DEFAULT_MODEL
            , thinking: { type: "disabled" },
            
            messages: [
              { role: "system", content: "你是一个专业的妇产科知识库 AI。请针对用户的提问，给出科学、严谨、温暖的回答。格式：Markdown。回答要包含：1. 直接结论；2. 原因分析；3. 注意事项。**请务必将回答字数严格控制在150字以内，言简意赅。**" },
              { role: "user", content: args.query }
            ]
          })
        });
        
        const data = await res.json();
        const content = data.choices[0]?.message?.content || "暂时无法查询到相关信息，建议咨询医生。";

        return JSON.stringify({ 
          success: true, 
          type: "knowledge_card",
          data: {
            query: args.query,
            title: "孕期知识库",
            content: content
          },
          message: "查询成功，请查看上方卡片。"
        });
      } catch (e) {
        return JSON.stringify({ success: false, message: "知识库连接失败" });
      }
    },
    async recommend_products(args: any) {
      await new Promise(r => setTimeout(r, 1000));
      const products = [
        { name: "低GI全麦荞麦面", price: "29.9", tags: ["控糖专用", "高饱腹"], link: "#" },
        { name: "稳糖高蛋白牛奶", price: "69.0", tags: ["0蔗糖", "孕妇适用"], link: "#" },
        { name: "进口奇亚籽饼干", price: "35.5", tags: ["低卡", "解馋零食"], link: "#" }
      ];
      return JSON.stringify({ success: true, type: "product_list", category: args.category, data: products });
    },
    async find_nearby_services(args: any) {
      await new Promise(r => setTimeout(r, 1500));
      const services = [
        { name: "爱博恩高端月子中心", distance: "2.5km", price: "4.8w起", tags: ["控糖月子餐认证", "三甲医生巡房"], rating: 4.9 },
        { name: "金牌月嫂-王阿姨", distance: "全城", price: "1.2w/月", tags: ["5年控糖护理经验", "营养师证"], rating: 5.0 },
        { name: "和睦家医院产科", distance: "5.0km", price: "挂号费500", tags: ["GDM专科", "国际医疗"], rating: 4.8 }
      ];
      return JSON.stringify({ success: true, type: "service_list", service_type: args.service_type, data: services });
    },
    async get_checkup_schedule(args: any) {
      const current = args.current_week || 24;
      const currentDay = args.current_day || 0;
      const totalDays = current * 7 + currentDay;
      const weeks = Math.floor(totalDays / 7);
      const days = totalDays % 7;
      const displayWeek = days > 0 ? `${weeks}+${days}` : `${weeks}`;

      // 内置产检计划数据
      const CHECKUP_SCHEDULE = [
        {
          range: [6, 8],
          name: "早孕期",
          frequency: "1 次",
          items: "血 HCG + 孕酮、B 超（确认宫内孕 + 胎心）、血常规、尿常规、肝肾功能、甲状腺功能、乙肝/梅毒/艾滋病筛查",
          purpose: "确认妊娠状态，评估母体基础健康，排除早期异常",
          gdm_tip: "同步检测空腹血糖（≥5.1 mmol/L 需警惕），记录孕前 BMI（≥25 为 GDM 高危）"
        },
        {
          range: [11, 13],
          name: "NT 检查",
          frequency: "1 次",
          items: "NT 检查（胎儿颈后透明层）、早期唐氏筛查（或无创 DNA）、心电图",
          purpose: "筛查胎儿染色体异常风险，评估心脏功能",
          gdm_tip: "复查空腹血糖，若≥5.1 mmol/L 直接诊断 GDM，启动饮食干预"
        },
        {
          range: [16, 20],
          name: "中期唐筛",
          frequency: "1 次",
          items: "中期唐氏筛查（无创 DNA 高风险者需羊水穿刺）、尿常规",
          purpose: "进一步筛查胎儿染色体异常",
          gdm_tip: "每 2 周监测 1 次空腹 + 餐后 2 小时血糖，记录饮食运动日志"
        },
        {
          range: [20, 24],
          name: "大排畸",
          frequency: "1 次",
          items: "大排畸 B 超（胎儿结构畸形筛查）、血常规（复查贫血）",
          purpose: "排查胎儿器官结构异常，监测母体贫血情况",
          gdm_tip: "血糖控制目标：空腹＜5.1、餐后 1 小时＜7.8、餐后 2 小时＜6.7 mmol/L"
        },
        {
          range: [24, 28],
          name: "OGTT 糖耐",
          frequency: "1 次（关键节点）",
          items: "口服葡萄糖耐量试验（OGTT）、尿常规、肝肾功能复查",
          purpose: "确诊 GDM（OGTT：空腹≥5.1、1 小时≥10.0、2 小时≥8.5 mmol/L 任一达标即诊断）",
          gdm_tip: "未确诊者完成 OGTT；已确诊者复查糖化血红蛋白（目标＜37 mmol/mol）"
        },
        {
          range: [30, 32],
          name: "小排畸",
          frequency: "1 次",
          items: "小排畸 B 超（胎儿生长发育评估）、血常规、尿常规、胎心监护（NST）",
          purpose: "监测胎儿生长速度，排查晚期结构异常，评估胎儿储备功能",
          gdm_tip: "每周监测 2-3 次全天血糖（空腹 + 三餐后 2 小时），若控制不佳需就医评估是否使用胰岛素"
        },
        {
          range: [34, 36],
          name: "孕晚期",
          frequency: "每 2 周 1 次",
          items: "胎心监护（NST）、尿常规、B 超（羊水指数 + 胎儿胎位）",
          purpose: "持续评估胎儿安危，判断分娩方式可行性",
          gdm_tip: "每周复查 1 次空腹血糖，每 2 周复查糖化血红蛋白，监测胎儿大小（避免巨大儿）"
        },
        {
          range: [37, 40],
          name: "分娩前评估",
          frequency: "每周 1 次",
          items: "胎心监护（NST）、B 超（羊水 + 胎盘功能）、宫颈成熟度评估",
          purpose: "确定分娩时机，评估胎盘功能，判断是否具备顺产条件",
          gdm_tip: "每日监测 4 次血糖（空腹 + 三餐后 2 小时），血糖波动过大需提前终止妊娠"
        },
        {
          range: [999, 999],
          name: "产后 42 天复查",
          frequency: "1 次",
          items: "血糖复查（空腹 + 餐后 2 小时）、妇科检查、B 超（子宫复旧情况）、盆底肌评估",
          purpose: "评估产后恢复情况",
          gdm_tip: "关注产后血糖恢复情况，GDM 患者产后 6-12 周需复查 OGTT"
        }
      ];

      // 查找当前孕周对应的产检阶段
      const currentPhase = CHECKUP_SCHEDULE.find(phase => weeks >= phase.range[0] && weeks <= phase.range[1]);
      // 查找下一个产检阶段
      const nextPhaseIndex = CHECKUP_SCHEDULE.findIndex(phase => weeks < phase.range[0] || (phase.range[0] === 999 && weeks >= 37));
      const nextPhase = CHECKUP_SCHEDULE[nextPhaseIndex];

      // 计算下次产检倒计时
      let countdown = "";
      if (nextPhase) {
        if (nextPhase.range[0] === 999) {
          countdown = "产后 42 天复查";
        } else {
          const nextWeek = nextPhase.range[0];
          const daysUntil = (nextWeek - weeks) * 7 - days;
          if (daysUntil > 0) {
            countdown = `还有 ${daysUntil} 天`;
          } else {
            countdown = "即将/已错过最佳检查时间，建议尽快就医";
          }
        }
      }

      // 生成完整时间表
      const schedule = CHECKUP_SCHEDULE.filter(p => p.range[0] !== 999).map(p =>
        `孕 ${p.range[0]}-${p.range[1]} 周 | ${p.name} | ${p.items}`
      ).join("\n");

      // 本阶段注意事项
      const tips = currentPhase?.gdm_tip || "按时产检，注意胎动";

      return JSON.stringify({
        success: true,
        type: "checkup_card",
        data: {
          current_week: current,
          current_day: currentDay,
          display_week: displayWeek,
          current_phase: currentPhase,
          next_checkup: nextPhase ? `孕 ${nextPhase.range[0]}-${nextPhase.range[1]} 周：${nextPhase.name}` : "产后复查",
          countdown,
          preparation: "产检当天需空腹（血糖相关检查）、携带产检手册、穿方便穿脱的衣物",
          tips,
          schedule
        }
      });
    }
  };

  const runAgent = async (userMsg: string) => {
    if (!apiKey) { setShowSettings(true); return; }
    if(steps.some(s => s.status === 'running')) return;

    setInput("");
    setSteps([{ label: "小暖正在思考...", status: "running" }]); 
    setMessages(prev => [...prev, { id: Date.now().toString(), role: 'user', content: userMsg }]);
    let executedToolName: string | null = null;

    try {
      const pg = calculatePregnancyWeek(contextRef.current.profile.last_menstrual_date);
      const todayStr = ["周日", "周一", "周二", "周三", "周四", "周五", "周六"][new Date().getDay()];
      
      const sysPrompt = `你是孕糖小暖，孕期控糖助手。用户: 孕${pg?.weeks||'?'}周${pg?.days||0}天，末次月经:${contextRef.current.profile.last_menstrual_date||'未设置'}，位置:${contextRef.current.profile.location||'未知'}，今天是:${todayStr}。

**【意图判断规则】先判断用户意图，再选择工具：**
1. 直接回复（不调用工具）：闲聊、问候、表达情绪、简单的鼓励/祝福、基于已有信息的简单查询
   - 例："你好"、"今天心情不好"、"谢谢你"
   - 例："我现在怀孕几周了？"（根据 profile 中的末次月经直接计算）
   - 例："我的预产期是什么时候？"（同上）
2. 调用工具：涉及具体操作或需要从外部获取数据

**【工具使用规则】**

**一、食谱管理 (manage_meal_plan)**
- action="get": 用户想"生成/查看/看看/给我看一下今天的/本周的食谱"
- action="create": 用户说"重置/重新生成/换一个全新的食谱"
- action="update": 用户说"把XX改成XX/替换/换掉/不想吃XX，想吃XX"
  ⚠️ **注意**：用户只说"我想换食谱"不要理解成 create，这通常是想要修改
  ⚠️ **重要**：构造 updates 时，key 必须是中文"周一"、"早餐"，value 是完整的新食物列表

**二、运动管理 (manage_exercise_plan)**
- 规则同上：get=查看,生成，create=重置，update=修改
- 例："今天的运动是什么"→get；"把周一的散步改成瑜伽"→update

**三、知识库 (search_knowledge_base)**
用于**医学/健康相关问题**，包括：
- 能不能吃 XX（螃蟹、咖啡、冰激凌...）
- 症状原因（腿抽筋、头晕、失眠...）
- 注意事项（感冒能吃药吗、可以做飞机吗...）
⚠️ **不要用于**：闲聊、情感问题、简单的问候

**四、血糖记录 (record_blood_sugar)**
- 用户明确说"血糖是X.X"或"记录血糖"时使用
- 提取数值和测量类型（ fasting / postprandial_1h / postprandial_2h / random ）

**五、档案更新 (update_user_profile)**
- 用户说"末次月经是X月X日"或修改身高/体重/过敏原时使用
- 必须将日期转为 YYYY-MM-DD 格式

**六、产检计划 (get_checkup_schedule)**
- 用户问"下次产检是什么时候"、"产检安排"时使用

**七、商品推荐 (recommend_products)**
- 用户问"有什么控糖零食推荐"、"买什么面条"时使用

**八、周边服务 (find_nearby_services)**
- 用户问"附近有哪些月嫂/妇产医院/月子中心"时使用

**【预警规则】**

**一、血糖指标与响应逻辑**

| 类型 | 目标 | 报警阈值 | 响应策略 |
|------|------|----------|----------|
| 空腹血糖 | < 5.1 | ≥ 5.1 | 连续2次超标→触发夜间加餐调整（优质蛋白+复合碳水）；持续超标→推送就医提醒 |
| 餐后1h血糖 | < 7.8 | ≥ 7.8 | 单次超标→回顾主食类型/烹饪方式，标记高GI食材；连续超标→降低精制碳水配额 |
| 餐后2h血糖 | < 6.7 | ≥ 6.7 | 达标但饥饿→计算热量缺口，推荐低GI中式加餐（如1小把原味坚果、半根玉米） |
| 糖化血红蛋白 | < 37 | ≥ 37 | 趋势预警，提示饮食运动干预不足，建议就医调整方案 |
| 低血糖 Level 1 | < 3.3 | < 3.3 | **最高优先级**：停止所有运动建议，推送"15-15原则"补糖方案（15g葡萄糖，15分钟后复测） |
| 低血糖 Level 2 | < 3.0 | < 3.0 | **紧急**：立即就医，伴随意识模糊风险 |

**二、产检提醒**：距离下次产检不足3天时提醒

**三、风险提醒**：用户描述的症状可能存在风险时（如严重头痛、视力模糊、出血、胎动异常等）
⚠️ **预警必须放在回复开头**，用 ⚠️ 标记

**【回复规则】**
- 工具调用成功后，**不要重复卡片内容**，只说"已为您更新，请查看上方卡片"
- 先判断意图，不要过度工具调用
- 工具调用成功后只说"已为您更新，请查看上方卡片"`
;

      const historyPayload = messages
        .filter(m => m.id !== 'init')
        .map(m => {
          let content = m.content;
          if (m.role === 'assistant' && m.rawData) {
             try {
               const data = JSON.parse(m.rawData);
               const type = data.type || "data";
               content += `\n\n(System Note: User has received the ${type} card. Do not repeat details.)`;
             } catch(e) {
               content += `\n\n(System Note: Data card displayed.)`;
             }
          }
          return { role: m.role, content };
        });

      const processTurn = async (messageHistory: any[], depth = 0): Promise<any> => {
        if (depth > 5) {
          return { content: "抱歉，任务过于复杂，我暂时无法继续处理。", rawData: null };
        }

        const res = await fetch(API_URL, {
          method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${apiKey}` },
          body: JSON.stringify({ model: DEFAULT_MODEL, thinking: { type: "disabled" },messages: messageHistory, tools: DOUBAO_TOOLS })
        });
        
        const data = await res.json();
        const choice = data.choices[0];
        const message = choice.message;

        if (message.tool_calls) {
          const toolCall = message.tool_calls[0];
          const fnName = toolCall.function.name;
          const args = JSON.parse(toolCall.function.arguments);
          const friendlyName = TOOL_NAMES[fnName] || fnName;
          
          executedToolName = friendlyName;
          setSteps(prev => {
            const newSteps = [...prev];
            if(newSteps.length > 0) newSteps[newSteps.length - 1].status = 'done';
            newSteps.push({ label: `正在为您${friendlyName}...`, status: "running" }); 
            return newSteps;
          });

          // 执行工具 (使用优化后的 toolsImpl)
          const result = await (toolsImpl as any)[fnName](args);
          
          setSteps(prev => {
            const newSteps = [...prev];
            if(newSteps.length > 0) newSteps[newSteps.length - 1].status = 'done';
            newSteps.push({ label: "正在整理回答...", status: "running" }); 
            return newSteps;
          });

          return await processTurn([...messageHistory, message, { role: "tool", tool_call_id: toolCall.id, content: result }], depth + 1);
        } else {
          const text = message.content;
          let rawData = null;
          const lastMsg = messageHistory[messageHistory.length - 1];
          if (lastMsg?.role === 'tool') {
             try { rawData = JSON.stringify(JSON.parse(lastMsg.content)); } catch(e) { rawData = lastMsg.content; }
          }
          
          setSteps(prev => {
            const newSteps = [...prev];
            const lastStep = newSteps[newSteps.length - 1];
            lastStep.status = 'done';
            // 优化：如果没调用工具，说明是直接回复，不显示"已完成"
            if (executedToolName) {
              lastStep.label = `${executedToolName}已完成`;
            } else {
              // 移除最后一步，因为没有工具调用过程
              newSteps.pop();
            }
            return newSteps;
          });
          return { content: text, rawData };
        }
      };

      const finalHistory = [
        { role: "system", content: sysPrompt },
        ...historyPayload,
        { role: "user", content: userMsg }
      ];

      const finalRes = await processTurn(finalHistory);
      setMessages(prev => [...prev, { id: Date.now().toString(), role: 'assistant', content: finalRes.content, rawData: finalRes.rawData }]);
    } catch (e: any) {
      console.error(e);
      setMessages(prev => [...prev, { id: Date.now().toString(), role: 'assistant', content: `错误: ${e.message}` }]);
      setSteps(prev => [...prev, { label: "系统繁忙", status: 'done' }]);
    }
  };

  // --- 新增：欢迎卡片组件 ---
  const WelcomeCard = ({ onAction }: { onAction: (text: string) => void }) => {
    const QUESTIONS = [
      '我需要设置我的孕期档案吗？',
      '我现在怀孕几周了？',
      '心情不好求安慰', // 增加一个纯闲聊的示例
      '请给我制定一个孕期食谱'
    ];

    return (
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-emerald-100 max-w-sm animate-in fade-in slide-in-from-bottom-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-gradient-to-tr from-emerald-400 to-emerald-200 rounded-full shadow-md overflow-hidden">
             <img src={`${process.env.PUBLIC_URL}/孕糖小暖头像.png`} alt="孕糖小暖" className="w-full h-full object-cover" />
          </div>
          <div>
            <div className="font-bold text-lg text-slate-800">孕糖小暖</div>
            <div className="text-xs text-emerald-600 font-medium">AI 孕期控糖专家</div>
          </div>
        </div>
        
        <div className="text-sm text-slate-600 leading-relaxed mb-6 space-y-2">
          <p>👋 你好呀！我是您的一站式控糖助手，不仅能管理血糖和食谱，还能为您：</p>
          <ul className="space-y-1 pl-1">
            <li className="flex items-center gap-2"><Activity size={14} className="text-red-500"/> 记录血糖数值</li>
            <li className="flex items-center gap-2"><ChefHat size={14} className="text-emerald-500"/> 生成孕期食谱</li>
            <li className="flex items-center gap-2"><Stethoscope size={14} className="text-blue-500"/> 规划产检时间</li>
          </ul>
          <p className="text-xs text-slate-400 pt-2">请先告诉我您的末次月经，我们开始吧！</p>
        </div>

        <div className="space-y-2">
          <div className="text-xs font-bold text-slate-400 mb-2 flex items-center gap-1"><MessageSquare size={12}/> 猜您想问：</div>
          {QUESTIONS.map((q, i) => (
            <button 
              key={i}
              onClick={() => onAction(q)}
              className="w-full text-left text-xs px-4 py-3 bg-slate-50 hover:bg-emerald-50 text-slate-600 hover:text-emerald-700 rounded-xl transition-all duration-200 border border-transparent hover:border-emerald-100 flex items-center justify-between group"
            >
              {q === '心情不好求安慰' ? <span className="flex items-center gap-2"><HeartHandshake size={14} className="text-pink-400"/> {q}</span> : q}
              <ArrowRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity text-emerald-400 -translate-x-2 group-hover:translate-x-0"/>
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-screen bg-[#FDFBF7] font-sans text-slate-700 relative">
      <header className="bg-white/90 backdrop-blur border-b border-orange-100 px-4 py-3 flex justify-between items-center z-10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-emerald-400 to-emerald-200 shadow-md overflow-hidden">
            <img src={`${process.env.PUBLIC_URL}/孕糖小暖头像.png`} alt="孕糖小暖" className="w-full h-full object-cover" />
          </div>
          <div>
            <h1 className="font-bold text-lg text-slate-800">孕糖小暖</h1>
            <div className="text-xs text-slate-500">
              {context.profile.last_menstrual_date 
                ? `孕 ${calculatePregnancyWeek(context.profile.last_menstrual_date)?.weeks} 周` 
                : "未设置孕周"}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowProfile(true)} className="p-2 hover:bg-slate-100 rounded-full"><UserCircle size={20} className="text-slate-400"/></button>
          {/* <button onClick={() => setShowSettings(true)} className="p-2 hover:bg-slate-100 rounded-full"><Settings size={20} className="text-slate-400"/></button> */}
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-4 scroll-smooth">
        <div className="max-w-2xl mx-auto space-y-6">
          {messages.map((msg, index) => (
            <React.Fragment key={msg.id}>
              {msg.id === 'init' ? (
                <div className="flex justify-center py-4">
                  <WelcomeCard onAction={(text) => runAgent(text)} />
                </div>
              ) : (
                <div className={cn("flex gap-3", msg.role === 'user' ? "flex-row-reverse" : "flex-row")}>
                  <div className={cn("w-9 h-9 rounded-full flex items-center justify-center shrink-0 mt-1 shadow-sm border overflow-hidden", msg.role === 'user' ? "bg-orange-100 text-orange-600 border-orange-200" : "bg-white border-emerald-100")}>
                    {msg.role === 'user' ? <User size={16}/> : <img src={`${process.env.PUBLIC_URL}/孕糖小暖头像.png`} alt="孕糖小暖" className="w-full h-full object-cover" />}
                  </div>
                  <div className={cn("flex flex-col max-w-[90%]", msg.role === 'user' ? "items-end" : "items-start")}>
                    {msg.role === 'assistant' && msg.rawData && <DataCard rawJson={msg.rawData} fullContext={context} />}
                    <div className={cn("px-4 py-3 rounded-2xl shadow-sm text-sm leading-relaxed", msg.role === 'user' ? "bg-orange-500 text-white rounded-tr-none" : "bg-white border border-emerald-50 text-slate-600 rounded-tl-none")}>
                      <ReactMarkdown 
                        components={{
                          p: ({node, ...props}) => <p className="mb-1 last:mb-0" {...props} />,
                          ul: ({node, ...props}) => <ul className="list-disc list-inside mb-1 space-y-0.5" {...props} />,
                          ol: ({node, ...props}) => <ol className="list-decimal list-inside mb-1 space-y-0.5" {...props} />,
                          li: ({node, ...props}) => <li className="marker:text-indigo-400" {...props} />,
                          strong: ({node, ...props}) => <strong className="font-bold text-indigo-900" {...props} />,
                        }}
                      >{msg.content}</ReactMarkdown>
                    </div>
                  </div>
                </div>
              )}
              {index === lastUserIndex && steps.length > 0 && (
                <div className="pl-12 pr-4 animate-in fade-in slide-in-from-top-2">
                  <ProcessingTimeline steps={steps} />
                </div>
              )}
            </React.Fragment>
          ))}
          <div ref={messagesEndRef}/>
        </div>
      </main>

      <footer className="bg-white p-4 border-t border-orange-50">
        <div className="max-w-2xl mx-auto flex gap-2">
          <input className="flex-1 bg-slate-50 border-0 rounded-full pl-5 py-3 text-sm focus:ring-2 focus:ring-emerald-100 outline-none" placeholder={apiKey ? "试着问：买什么面条控糖？" : "请配置API Key"} value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && steps.every(s => s.status === 'done') && runAgent(input)} disabled={steps.some(s => s.status === 'running')}/>
          <button onClick={() => runAgent(input)} disabled={!input.trim() || steps.some(s => s.status === 'running')} className="w-11 h-11 bg-emerald-500 text-white rounded-full flex items-center justify-center hover:shadow-lg disabled:opacity-50"><Send size={18}/></button>
        </div>
      </footer>

      {showSettings && (
        <div className="absolute inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2"><KeyRound size={20} className="text-emerald-500"/> 设置 Doubao API Key</h3>
            <input type="password" value={apiKey} onChange={e => setApiKey(e.target.value)} placeholder="ApiKey..." className="w-full border rounded-xl p-3 text-sm focus:ring-2 focus:ring-emerald-500 outline-none mb-4"/>
            <button onClick={() => {localStorage.setItem("doubao_api_key", apiKey); setShowSettings(false);}} className="w-full bg-emerald-500 text-white font-bold py-3 rounded-xl">保存</button>
          </div>
        </div>
      )}

      {showProfile && (
        <div className="absolute inset-0 bg-black/30 backdrop-blur-sm z-50 flex justify-end animate-in fade-in">
          <div className="w-full max-w-md bg-white h-full shadow-2xl p-6 flex flex-col animate-in slide-in-from-right duration-300">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-bold text-xl text-slate-800 flex items-center gap-2">
                <UserCircle className="text-emerald-500"/> 个人中心
              </h2>
              <button onClick={() => setShowProfile(false)} className="p-1 hover:bg-slate-100 rounded-full"><X size={24} className="text-slate-400"/></button>
            </div>

            <div className="flex-1 overflow-y-auto space-y-4 no-scrollbar">

              {/* 孕期信息 */}
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-4 rounded-2xl border border-emerald-100">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm text-2xl">🤰</div>
                    <div>
                      <div className="font-bold text-slate-800">准妈妈</div>
                      <div className="text-xs text-slate-500">
                        {context.profile.last_menstrual_date
                          ? `孕 ${calculatePregnancyWeek(context.profile.last_menstrual_date)?.weeks} 周 ${calculatePregnancyWeek(context.profile.last_menstrual_date)?.days} 天`
                          : "孕周未设置"}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-white/60 p-2 rounded-lg mb-3">
                  <div className="text-slate-400 text-[10px] mb-1">末次月经日期</div>
                  <input
                    type="date"
                    value={context.profile.last_menstrual_date || ""}
                    onChange={(e) => {
                      const lmp = e.target.value;
                      const pg = calculatePregnancyWeek(lmp);
                      if (pg && !pg.error) {
                        setContext(prev => ({
                          ...prev,
                          profile: { ...prev.profile, last_menstrual_date: pg.formattedLMP || null }
                        }));
                      }
                    }}
                    className="w-full text-sm border rounded px-2 py-1"
                  />
                </div>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div className="bg-white/60 p-2 rounded-lg text-center">
                    <div className="text-slate-400 text-[10px]">预产期</div>
                    <div className="font-medium text-slate-700 text-xs">
                      {context.profile.last_menstrual_date ? calculatePregnancyWeek(context.profile.last_menstrual_date)?.dueDate : "-"}
                    </div>
                  </div>
                  <div className="bg-white/60 p-2 rounded-lg text-center">
                    <div className="text-slate-400 text-[10px]">BMI</div>
                    <div className="font-medium text-slate-700 text-xs">
                      {(context.profile.weight_kg / Math.pow(context.profile.height_cm/100, 2)).toFixed(1)}
                    </div>
                  </div>
                  <div className="bg-white/60 p-2 rounded-lg text-center">
                    <div className="text-slate-400 text-[10px]">血糖记录</div>
                    <div className="font-medium text-slate-700 text-xs">
                      {context.blood_sugar_history.length} 次
                    </div>
                  </div>
                </div>
              </div>

              {/* 血糖记录 */}
              <div>
                <h3 className="font-bold text-slate-700 mb-2 flex items-center gap-2 text-sm">
                  <Activity size={16}/> 血糖记录
                </h3>
                {context.blood_sugar_history.length > 0 ? (
                  <div className="bg-white border border-slate-100 rounded-xl p-3 shadow-sm">
                    {/* 统计摘要 */}
                    <div className="flex gap-3 mb-3">
                      <div className="flex-1 bg-red-50 rounded-lg p-2 text-center">
                        <div className="text-xs text-red-500">最近一次</div>
                        <div className="text-lg font-bold text-red-600">
                          {(() => {
                            const last = context.blood_sugar_history[context.blood_sugar_history.length - 1];
                            return last ? `${last.value}` : "-";
                          })()}
                        </div>
                        <div className="text-[10px] text-red-400">mmol/L</div>
                      </div>
                      <div className="flex-1 bg-orange-50 rounded-lg p-2 text-center">
                        <div className="text-xs text-orange-500">记录次数</div>
                        <div className="text-lg font-bold text-orange-600">{context.blood_sugar_history.length}</div>
                        <div className="text-[10px] text-orange-400">次</div>
                      </div>
                      <div className="flex-1 bg-emerald-50 rounded-lg p-2 text-center">
                        <div className="text-xs text-emerald-500">达标率</div>
                        <div className="text-lg font-bold text-emerald-600">
                          {(() => {
                            const records = context.blood_sugar_history;
                            const normal = records.filter(r => r.note === "正常").length;
                            return records.length > 0 ? Math.round((normal / records.length) * 100) : 0;
                          })()}%
                        </div>
                      </div>
                    </div>
                    {/* 最近5条记录 */}
                    <div className="space-y-1">
                      {context.blood_sugar_history.slice(-5).reverse().map((record, idx) => {
                        const typeLabels: Record<string, string> = {
                          fasting: "空腹",
                          postprandial_1h: "餐后1h",
                          postprandial_2h: "餐后2h",
                          random: "随机"
                        };
                        const date = new Date(record.timestamp);
                        const dateStr = `${date.getMonth() + 1}/${date.getDate()}`;
                        const isNormal = record.note === "正常";

                        return (
                          <div key={idx} className="flex items-center justify-between text-xs bg-slate-50 p-2 rounded">
                            <div className="flex items-center gap-2">
                              <span className={cn("w-1 h-4 rounded-full", isNormal ? "bg-emerald-400" : "bg-red-400")} />
                              <span className="text-slate-500">{dateStr}</span>
                              <span className="text-slate-400">{typeLabels[record.measure_type]}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={cn("font-medium", isNormal ? "text-emerald-600" : "text-red-600")}>
                                {record.value}
                              </span>
                              <span className={cn("text-[10px] px-1.5 py-0.5 rounded", isNormal ? "bg-emerald-100 text-emerald-600" : "bg-red-100 text-red-600")}>
                                {record.note}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm text-center">
                    <Activity size={24} className="mx-auto text-slate-300 mb-2" />
                    <div className="text-xs text-slate-400">暂无血糖记录</div>
                    <div className="text-[10px] text-slate-300 mt-1">记录血糖有助于控制孕期健康</div>
                  </div>
                )}
              </div>

              {/* 今日计划 */}
              <div>
                <h3 className="font-bold text-slate-700 mb-2 flex items-center gap-2 text-sm">
                  <Calendar size={16}/> 今日计划
                </h3>

                <div className="bg-white border border-slate-100 rounded-xl p-3 shadow-sm mb-2">
                  <div className="text-xs font-bold text-orange-500 mb-2 flex items-center gap-1">
                    <ChefHat size={14}/> 推荐食谱
                  </div>
                  <div className="space-y-1">
                    {(() => {
                      const todayKey = ["周日","周一","周二","周三","周四","周五","周六"][new Date().getDay()];
                      const meals = context.meal_plan[todayKey] || {};
                      return Object.entries(meals).map(([type, items]) => (
                        <div key={type} className="flex text-xs">
                          <span className="w-10 text-slate-400 shrink-0">{type}</span>
                          <span className="text-slate-700 truncate">{(items as MealItem[]).map(i => i.name).join('+')}</span>
                        </div>
                      ));
                    })()}
                  </div>
                </div>

                <div className="bg-white border border-slate-100 rounded-xl p-3 shadow-sm">
                  <div className="text-xs font-bold text-blue-500 mb-2 flex items-center gap-1">
                    <Dumbbell size={14}/> 运动打卡
                  </div>
                  <div className="space-y-1">
                    {(() => {
                      const todayKey = ["周日","周一","周二","周三","周四","周五","周六"][new Date().getDay()];
                      const exercises = context.exercise_plan[todayKey]?.items || [];
                      return exercises.length > 0 ? exercises.map((ex, idx) => (
                        <div key={idx} className="flex justify-between items-center text-xs bg-slate-50 p-2 rounded">
                          <span className="text-slate-700">{ex.activity}</span>
                          <span className="text-slate-400">{ex.duration}</span>
                        </div>
                      )) : <div className="text-xs text-slate-400 text-center py-2">今日休息</div>;
                    })()}
                  </div>
                </div>
              </div>

              {/* 档案设置 - 放最下面 */}
              <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
                <h3 className="font-bold text-slate-700 mb-3 flex items-center gap-2 text-sm">
                  <Settings size={16}/> 档案设置
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">身高</span>
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        value={context.profile.height_cm}
                        onChange={(e) => setContext(p => ({...p, profile: {...p.profile, height_cm: Number(e.target.value)}}))}
                        className="w-16 text-right text-sm border rounded px-2 py-1"
                      />
                      <span className="text-xs text-slate-400">cm</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">孕前体重</span>
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        value={context.profile.weight_kg}
                        onChange={(e) => setContext(p => ({...p, profile: {...p.profile, weight_kg: Number(e.target.value)}}))}
                        className="w-16 text-right text-sm border rounded px-2 py-1"
                      />
                      <span className="text-xs text-slate-400">kg</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">所在城市</span>
                    <input
                      type="text"
                      value={context.profile.location || ""}
                      onChange={(e) => setContext(p => ({...p, profile: {...p.profile, location: e.target.value}}))}
                      className="w-24 text-right text-sm border rounded px-2 py-1"
                      placeholder="北京"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-slate-500">过敏原</span>
                      <button
                        onClick={() => {
                          const newAllergy = prompt("请输入过敏原（用逗号分隔）:", context.profile.allergies.join(","));
                          if (newAllergy !== null) {
                            setContext(p => ({...p, profile: {...p.profile, allergies: newAllergy.split(",").map(a => a.trim()).filter(Boolean)}}));
                          }
                        }}
                        className="text-xs text-emerald-600 hover:text-emerald-700"
                      >
                        编辑
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {context.profile.allergies.length > 0 ? (
                        context.profile.allergies.map((a, i) => (
                          <span key={i} className="text-xs bg-red-50 text-red-600 px-2 py-0.5 rounded-full">{a}</span>
                        ))
                      ) : (
                        <span className="text-xs text-slate-400">未设置</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// --- 时间轴组件 ---
function ProcessingTimeline({ steps }: { steps: TimelineStep[] }) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const isFinished = steps.length > 0 && steps.every(s => s.status === 'done');
  
  useEffect(() => {
    if (isFinished) {
      const timer = setTimeout(() => { setIsCollapsed(true); }, 1500); 
      return () => clearTimeout(timer);
    } else { setIsCollapsed(false); }
  }, [isFinished, steps.length]);

  if (steps.length === 0) return null;

  return (
    <div className={cn("border border-emerald-100 bg-white rounded-xl shadow-sm overflow-hidden transition-all duration-500 ease-in-out w-fit min-w-[240px]", isCollapsed ? "border-transparent bg-transparent shadow-none" : "")}>
      <div className={cn("px-4 py-2.5 flex items-center justify-between cursor-pointer transition-colors rounded-xl", isCollapsed ? "bg-emerald-50 text-emerald-700 px-3 py-1.5 w-fit" : "hover:bg-slate-50 bg-slate-50/50 border-b border-emerald-50")} onClick={() => setIsCollapsed(!isCollapsed)}>
        <div className="flex items-center gap-2 text-xs font-semibold">
          {isFinished ? <CheckCircle2 size={14} className="text-emerald-500" /> : <Loader2 size={14} className="text-orange-500 animate-spin" />}
          {isCollapsed ? <span>{steps[steps.length - 1].label}</span> : <span>正在处理中 ({steps.filter(s => s.status === 'done').length}/{steps.length})</span>}
        </div>
        {!isCollapsed && <ChevronUp size={14} className="text-slate-400" />}
      </div>
      <div className={cn("transition-all duration-500 ease-in-out overflow-hidden bg-white", isCollapsed ? "max-h-0 opacity-0" : "max-h-[300px] opacity-100")}>
        <div className="p-3 space-y-4">
          {steps.map((step, idx) => (
            <div key={idx} className="flex items-center gap-3 relative">
              {idx !== steps.length - 1 && <div className={cn("absolute left-2.5 top-5 w-0.5 h-full -mb-3", step.status === 'done' ? "bg-emerald-200" : "bg-slate-100")} />}
              <div className="z-10">{step.status === 'done' ? <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center shrink-0"><CheckCircle2 size={12} className="text-emerald-600" /></div> : step.status === 'running' ? <div className="w-5 h-5 rounded-full bg-orange-100 flex items-center justify-center shrink-0"><Loader2 size={12} className="text-orange-600 animate-spin" /></div> : <div className="w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center shrink-0"><Circle size={8} className="text-slate-400" /></div>}</div>
              <div className="flex-1"><p className={cn("text-xs font-medium transition-colors", step.status === 'done' ? "text-slate-700" : step.status === 'running' ? "text-orange-600" : "text-slate-400")}>{step.label}</p></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// --- 卡片组件 ---
function DataCard({ rawJson, fullContext }: { rawJson: string, fullContext: UserContext }) {
  const [activeTab, setActiveTab] = useState("周一");
  const DAYS = ["周一", "周二", "周三", "周四", "周五", "周六", "周日"];

  try {
    const res = JSON.parse(rawJson);
    
    // 0. 用户档案
    if (res.type === "user_profile") {
      const { data } = res;
      return (
        <div className="mb-3 w-full bg-white border border-purple-100 rounded-xl overflow-hidden shadow-sm animate-in zoom-in-95">
          <div className="bg-purple-50 px-4 py-3 border-b border-purple-100 flex justify-between items-center"><div className="flex items-center gap-2 text-purple-800 font-bold text-sm"><User size={18}/> 个人档案</div><div className="text-xs text-purple-600 bg-white/50 px-2 py-1 rounded">已更新</div></div>
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"><div className="flex items-center gap-2 text-sm text-slate-600"><Calendar size={16} className="text-purple-400"/> 末次月经</div><div className="font-semibold text-slate-800">{data.last_menstrual_date || "未设置"}</div></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-purple-50/50 rounded-lg text-center"><div className="text-xs text-purple-400 mb-1 flex items-center justify-center gap-1"><Baby size={14}/> 当前孕周</div><div className="text-lg font-bold text-purple-700">{data.current_week}<span className="text-xs font-normal">周</span>{data.current_day}<span className="text-xs font-normal">天</span></div></div>
              <div className="p-3 bg-blue-50/50 rounded-lg text-center"><div className="text-xs text-blue-400 mb-1 flex items-center justify-center gap-1"><Calendar size={14}/> 预产期</div><div className="text-sm font-bold text-blue-700 mt-1">{data.due_date}</div></div>
            </div>
          </div>
        </div>
      );
    }

    // 1. 食谱卡片
    if (res.type === "meal_plan") {
      const plan = fullContext.meal_plan;
      const currentDayPlan = plan[activeTab] || {};
      return (
        <div className="mb-3 w-full bg-white border border-emerald-100 rounded-xl overflow-hidden shadow-sm animate-in zoom-in-95">
          <div className="bg-emerald-50 px-4 py-3 border-b border-emerald-100 flex justify-between items-center"><div className="flex items-center gap-2 text-emerald-800 font-bold text-sm"><ChefHat size={18}/> 本周食谱</div></div>
          <div className="flex overflow-x-auto border-b border-slate-100 no-scrollbar">{DAYS.map(day => (<button key={day} onClick={() => setActiveTab(day)} className={cn("px-3 py-2 text-xs font-medium whitespace-nowrap transition-colors", activeTab === day ? "text-emerald-600 border-b-2 border-emerald-500" : "text-slate-400 hover:text-slate-600")}>{day}</button>))}</div>
          <div className="p-4 space-y-4 max-h-60 overflow-y-auto">
            {Object.entries(currentDayPlan).map(([mealType, items]) => (<div key={mealType}><div className="text-xs font-bold text-orange-400 mb-2 uppercase tracking-wider">{mealType}</div><div className="space-y-2">{(items as MealItem[]).map((item, idx) => (<div key={idx} className="flex justify-between items-center text-sm bg-slate-50 p-2 rounded-lg"><span className="text-slate-700 font-medium">{item.name}</span><div className="text-xs text-slate-400 flex items-center gap-2"><span>{item.portion}</span><span className="flex items-center gap-0.5 text-orange-400"><Flame size={10}/> {item.calories}</span></div></div>))}</div></div>))}
          </div>
        </div>
      );
    }

    // 2. 运动计划
    if (res.type === "exercise_plan") {
      const plan = fullContext.exercise_plan;
      const currentDayEx = plan[activeTab];
      return (
        <div className="mb-3 w-full bg-white border border-blue-100 rounded-xl overflow-hidden shadow-sm animate-in zoom-in-95">
          <div className="bg-blue-50 px-4 py-3 border-b border-blue-100 flex justify-between items-center"><div className="flex items-center gap-2 text-blue-800 font-bold text-sm"><Dumbbell size={18}/> 运动计划</div></div>
          <div className="flex overflow-x-auto border-b border-slate-100 no-scrollbar">{DAYS.map(day => (<button key={day} onClick={() => setActiveTab(day)} className={cn("px-3 py-2 text-xs font-medium whitespace-nowrap transition-colors", activeTab === day ? "text-blue-600 border-b-2 border-blue-500" : "text-slate-400 hover:text-slate-600")}>{day}</button>))}</div>
          <div className="p-4 space-y-3">{!currentDayEx || currentDayEx.items.length === 0 ? <div className="text-center text-slate-400 text-xs py-4">本日休息</div> : (currentDayEx.items.map((item, idx) => (<div key={idx} className="flex justify-between items-center bg-slate-50 p-3 rounded-lg border border-slate-100"><div className="font-medium text-slate-700 text-sm">{item.activity}</div><div className="text-xs text-slate-500 flex flex-col items-end"><span>{item.duration}</span><span className="text-[10px] text-blue-400 border border-blue-200 px-1 rounded mt-0.5">{item.intensity}强度</span></div></div>)))}</div>
        </div>
      );
    }

    // 3. 血糖卡片
    if (res.type === "blood_sugar_card" || res.data?.value) {
      const data = res.type === "blood_sugar_card" ? res.data : res.data;
      const { value, status, measure_type, ai_analysis, action_items } = data as any;
      const typeLabels: Record<string, string> = {
        fasting: "空腹血糖",
        postprandial_1h: "餐后1h血糖",
        postprandial_2h: "餐后2h血糖",
        random: "随机血糖"
      };
      const typeLabel = typeLabels[measure_type] || "血糖";

      // 根据状态计算预警级别
      const getLevel = () => {
        if (status === 'critical_low') return { level: "emergency", label: "危急低血糖", bg: "bg-red-100", text: "text-red-700", border: "border-red-500", iconColor: "text-red-600" };
        if (status === 'critical_high') return { level: "emergency", label: "危急高血糖", bg: "bg-red-100", text: "text-red-700", border: "border-red-500", iconColor: "text-red-600" };
        if (status === 'warning') return { level: "warning", label: "偏高", bg: "bg-red-50", text: "text-red-600", border: "border-red-300", iconColor: "text-red-500" };
        return { level: "normal", label: "✓ 正常", bg: "bg-emerald-50", text: "text-emerald-600", border: "border-emerald-200", iconColor: "text-emerald-500" };
      };

      const level = getLevel();
      const isAlert = level.level !== "normal";

      return (
        <div className={cn(
          "mb-3 w-full bg-white border-2 p-5 rounded-xl shadow-md animate-in zoom-in-95",
          level.border,
          isAlert && (level.level === "emergency" ? "scale-105 shadow-lg shadow-red-200" : "scale-102")
        )}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={cn("p-3 rounded-full", level.bg)}>
                <Activity size={24} className={level.iconColor} />
              </div>
              <div>
                <span className="text-sm font-medium text-slate-500">{typeLabel}</span>
                <div className={cn("text-lg font-bold", level.text)}>{level.label}</div>
              </div>
            </div>
            {isAlert && (
              <span className={cn("text-xs font-bold px-2 py-1 rounded", level.bg, level.text)}>
                {status === 'critical_low' ? '⚠️ 危急' : status === 'critical_high' ? '⚠️ 危急' : '⚠️ 偏高'}
              </span>
            )}
          </div>

          <div className="flex items-baseline gap-3 mb-4">
            <span className={cn("text-5xl font-bold", level.text)}>{value}</span>
            <span className="text-lg text-slate-400">mmol/L</span>
          </div>

          {/* 智能归因分析 */}
          {ai_analysis && (
            <div className="bg-slate-50 p-4 rounded-lg mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles size={16} className="text-amber-500" />
                <span className="text-sm font-bold text-slate-700">智能归因</span>
              </div>
              <div className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{ai_analysis}</div>
            </div>
          )}

          {/* 危急状态紧急干预 */}
          {(status === 'critical_low' || status === 'critical_high') && (
            <div className="bg-red-100 p-4 rounded-lg mb-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">🚨</span>
                <span className="text-sm font-bold text-red-700">紧急干预</span>
              </div>
              {action_items && action_items.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {action_items.map((item: string, idx: number) => (
                    <span key={idx} className="text-xs bg-white text-red-600 px-3 py-1.5 rounded-full border border-red-200">
                      {item}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* 商业闭环：场景化推荐 */}
          {isAlert && (status === 'warning' || status === 'critical_high') && (
            <div className="pt-4 border-t border-slate-100">
              <div className="text-xs text-slate-400 mb-2 text-center">为您推荐控糖方案</div>
              <div className="flex gap-2">
                <button className="flex-1 bg-emerald-50 text-emerald-600 text-xs py-2.5 rounded-lg border border-emerald-100 flex items-center justify-center gap-1.5 hover:bg-emerald-100">
                  <Dumbbell size={14}/> 运动降糖
                </button>
                <button className="flex-1 bg-orange-50 text-orange-600 text-xs py-2.5 rounded-lg border border-orange-100 flex items-center justify-center gap-1.5 hover:bg-orange-100">
                  <ShoppingBag size={14}/> 低GI主食
                </button>
              </div>
            </div>
          )}

          {/* 低血糖紧急购买 */}
          {status === 'critical_low' && (
            <div className="pt-4 border-t border-slate-100">
              <button className="w-full bg-red-500 text-white text-xs py-3 rounded-lg font-medium shadow-sm hover:bg-red-600 flex items-center justify-center gap-2">
                <span>🚑</span> 一键购买葡萄糖补给
              </button>
            </div>
          )}
        </div>
      );
    }

    // 7. 知识库卡片 (NEW)
    if (res.type === "knowledge_card") {
        const { data } = res;
        return (
            <div className="mb-3 w-full bg-white border border-indigo-100 rounded-xl overflow-hidden shadow-sm animate-in zoom-in-95">
             <div className="bg-indigo-50 px-4 py-3 border-b border-indigo-100 flex justify-between items-center">
                <div className="flex items-center gap-2 text-indigo-800 font-bold text-sm"><BookOpen size={18}/> 知识库检索</div>
             </div>
             <div className="p-4">
               <div className="text-xs text-slate-400 mb-2">关键词：{data.query}</div>
               <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                  <ReactMarkdown 
                    components={{
                      p: ({node, ...props}) => <p className="mb-1 last:mb-0" {...props} />,
                      ul: ({node, ...props}) => <ul className="list-disc list-inside mb-1 space-y-0.5" {...props} />,
                      ol: ({node, ...props}) => <ol className="list-decimal list-inside mb-1 space-y-0.5" {...props} />,
                      li: ({node, ...props}) => <li className="marker:text-indigo-400" {...props} />,
                      strong: ({node, ...props}) => <strong className="font-bold text-indigo-900" {...props} />,
                    }}
                  >{data.content}</ReactMarkdown>
               </div>
             </div>
            </div>
        )
    }

    // 8. 产检计划卡片
    if (res.type === "checkup_card") {
      const { data } = res;
      return (
        <div className="mb-3 w-full bg-white border border-blue-100 rounded-xl overflow-hidden shadow-sm animate-in zoom-in-95">
          <div className="bg-blue-50 px-4 py-3 border-b border-blue-100 flex justify-between items-center">
            <div className="flex items-center gap-2 text-blue-800 font-bold text-sm"><Calendar size={18}/> 产检计划</div>
            <div className="text-xs text-blue-600 bg-white/50 px-2 py-1 rounded">孕 {data.display_week || data.current_week}周</div>
          </div>
          <div className="p-4 space-y-4">
            {/* 当前阶段 */}
            {data.current_phase && (
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-4 rounded-xl border border-emerald-100">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs text-emerald-500">当前阶段</div>
                  <div className="text-xs bg-white px-2 py-0.5 rounded text-emerald-600">{data.current_phase.frequency}</div>
                </div>
                <div className="font-bold text-emerald-800 text-lg mb-1">{data.current_phase.name}</div>
                <div className="text-xs text-emerald-600 mb-2">{data.current_phase.purpose}</div>
                <div className="text-xs bg-white/60 p-2 rounded text-slate-600">
                  <span className="font-medium">检查项目：</span>{data.current_phase.items}
                </div>
              </div>
            )}

            {/* 下次产检 */}
            {data.next_checkup && (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-100">
                <div className="text-xs text-blue-500 mb-1">下次产检</div>
                <div className="text-lg font-bold text-blue-800">{data.next_checkup}</div>
                {data.countdown && <div className="text-sm text-blue-600 mt-1">⏰ {data.countdown}</div>}
              </div>
            )}

            {/* 注意事项 */}
            {data.tips && (
              <div>
                <div className="text-xs font-bold text-slate-500 mb-2">💡 GDM 特殊注意</div>
                <div className="text-sm text-slate-700 bg-amber-50 p-3 rounded-lg">{data.tips}</div>
              </div>
            )}

            {/* 准备工作 */}
            {data.preparation && (
              <div>
                <div className="text-xs font-bold text-slate-500 mb-2">📋 产检准备</div>
                <div className="text-sm text-slate-700 bg-slate-50 p-3 rounded-lg">{data.preparation}</div>
              </div>
            )}

            {/* 完整时间表 */}
            {data.schedule && (
              <div>
                <div className="text-xs font-bold text-slate-500 mb-2">📅 完整产检时间表</div>
                <div className="text-xs text-slate-500 bg-slate-50 p-3 rounded-lg whitespace-pre-line">{data.schedule}</div>
              </div>
            )}
          </div>
        </div>
      );
    }

  } catch (e) { return null; }
  return null;
}